// Guarded service-worker registration.
// Only registers in production on real hosts. Refuses (and cleans up) anywhere else.

import { updateServiceWorkerSafely } from "@/lib/serviceWorker";

const APP_SW_PATH = "/sw.js";
const LEGACY_PRIVATE_CACHES = ["supabase-api"];

async function clearLegacyPrivateCaches(): Promise<void> {
  if (!("caches" in window)) return;
  try {
    await Promise.all(LEGACY_PRIVATE_CACHES.map((cacheName) => window.caches.delete(cacheName)));
  } catch {
    /* Cache cleanup must never interrupt app startup. */
  }
}

function shouldRefuseRegistration(): boolean {
  if (typeof window === "undefined") return true;
  if (!import.meta.env.PROD) return true;

  // iframe (Lovable editor preview, embeds, etc.)
  try {
    if (window.self !== window.top) return true;
  } catch {
    return true;
  }

  const url = new URL(window.location.href);
  if (url.searchParams.get("sw") === "off") return true;

  const host = window.location.hostname;
  const productionHosts = new Set([
    "geominer.ca",
    "www.geominer.ca",
    "geominer.lovable.app",
    "rocky-finds-field.lovable.app",
  ]);
  if (!productionHosts.has(host)) return true;

  const previewHosts = [
    /^id-preview--/,
    /^preview--/,
    /(^|\.)lovableproject\.com$/,
    /(^|\.)lovableproject-dev\.com$/,
    /(^|\.)beta\.lovable\.dev$/,
  ];
  if (previewHosts.some((re) => re.test(host))) return true;

  return false;
}

async function unregisterAppSW(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      regs
        .filter((r) => {
          const url =
            r.active?.scriptURL ||
            r.waiting?.scriptURL ||
            r.installing?.scriptURL ||
            "";
          return url.endsWith(APP_SW_PATH);
        })
        .map((r) => r.unregister())
    );
  } catch {
    /* noop */
  }
}

export function registerAppServiceWorker(): void {
  if (typeof window === "undefined") return;
  void clearLegacyPrivateCaches();
  if (!("serviceWorker" in navigator)) return;

  if (shouldRefuseRegistration()) {
    void unregisterAppSW();
    return;
  }

  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(APP_SW_PATH, { scope: "/", updateViaCache: "none" })
      .then((registration) => {
        void updateServiceWorkerSafely(registration);

        const updateWhenVisible = () => {
          if (document.visibilityState === "visible") void updateServiceWorkerSafely(registration);
        };
        document.addEventListener("visibilitychange", updateWhenVisible);
        window.setInterval(() => void updateServiceWorkerSafely(registration), 60 * 60 * 1000);
      })
      .catch(() => {
        /* ignore registration errors — app still works without SW */
      });
  });
}
